import {
  ProcessingStatusSchema,
  RenewalWindowSchema,
  type NotificationTier,
  type ProcessingStatus,
  type RenewalWindow,
} from '@clm/shared-types';

/**
 * CLM-90 — the copy and the one pure helper the strip and the list share.
 * Kept out of the component files so fast refresh stays whole
 * (`react-refresh/only-export-components`).
 */

export const TIER_LABELS: Readonly<Record<NotificationTier, string>> = {
  notice_90: '90-day notice',
  notice_60: '60-day notice',
  notice_30: '30-day notice',
  notice_7: '7-day notice',
  past_due: 'Past-due alert',
};

export const ATTENTION_CAPTION =
  'Due date is the renewal-notice date when the contract has one, otherwise the expiry date. Counts are your scope only.';

/**
 * CLM-132 — "Expiring" (below) and "Due" (the strip above) can disagree for
 * the same contract: "Due" reads the renewal-notice date when the contract
 * has one, falling back to expiry; "Expiring" always reads expiry_date,
 * regardless of a renewal-notice date. Disclosed rather than unified — which
 * date each tile should read is a product decision, not something to change
 * silently under an existing number.
 */
export const KPI_EXPIRING_CAPTION =
  'Expiring counts expiry date only. Due (above) counts the renewal-notice date when the contract has one — the two can disagree for the same contract.';

export const RENEWALS_EMPTY_COPY = 'Nothing in your scope is due inside this window.';

/** `?window=` → the enum, `'90'` when absent or malformed (D-90-3). */
export function windowFromParams(raw: string | null): RenewalWindow {
  const parsed = RenewalWindowSchema.safeParse(raw);
  return parsed.success ? parsed.data : '90';
}

/**
 * CLM-95 (D-95-3) — the dashboard's single expanded-section state, `?panel=`.
 * One id names exactly one tile across three otherwise-independent groups
 * (`AttentionStrip`'s due tiles, the KPI row, the status-mix legend), so the
 * page can enforce "one at a time" without those groups sharing anything
 * else. Encoded as a plain string in the URL (`due-30`, `kpi-active`,
 * `mix-Verified`, …) rather than a compound query key, so a shared link is
 * one readable parameter, not several that could drift out of sync.
 */
export type DashboardPanel =
  | { readonly kind: 'due'; readonly window: RenewalWindow }
  | { readonly kind: 'kpi-active' }
  | { readonly kind: 'kpi-expiring-30' }
  | { readonly kind: 'mix'; readonly status: ProcessingStatus };

const DUE_PREFIX = 'due-';
const MIX_PREFIX = 'mix-';

/** `?panel=` → the typed union, `null` when absent or malformed — nothing expanded. */
export function panelFromParams(raw: string | null): DashboardPanel | null {
  if (raw === null) return null;
  if (raw === 'kpi-active' || raw === 'kpi-expiring-30') return { kind: raw };
  if (raw.startsWith(DUE_PREFIX)) {
    const parsed = RenewalWindowSchema.safeParse(raw.slice(DUE_PREFIX.length));
    return parsed.success ? { kind: 'due', window: parsed.data } : null;
  }
  if (raw.startsWith(MIX_PREFIX)) {
    const parsed = ProcessingStatusSchema.safeParse(raw.slice(MIX_PREFIX.length));
    return parsed.success ? { kind: 'mix', status: parsed.data } : null;
  }
  return null;
}

export function panelToParam(panel: DashboardPanel): string {
  switch (panel.kind) {
    case 'due':
      return `${DUE_PREFIX}${panel.window}`;
    case 'mix':
      return `${MIX_PREFIX}${panel.status}`;
    default:
      return panel.kind;
  }
}

/** Same panel, same tile — used to decide "clicking the open tile again collapses it" (D-95-3). */
export function samePanel(a: DashboardPanel | null, b: DashboardPanel): boolean {
  return a !== null && panelToParam(a) === panelToParam(b);
}

/**
 * The DOM id of the tile/legend-chip button that opens `panel` — stable and
 * derived from `panelToParam` rather than tracked separately, so the trigger
 * a "Collapse" control focuses back to (D-95-3's own accessibility
 * requirement) can never drift out of sync with the id `panelFromParams`
 * would parse. The trigger element itself never unmounts (only the
 * disclosure section does), so this id is always resolvable when a section
 * is open.
 */
export function panelTriggerId(panel: DashboardPanel): string {
  return `clm95-tile-${panelToParam(panel)}`;
}

/**
 * The three fixed disclosure-section ids (D-95-3) — one per tile group,
 * shared between `AttentionStrip.tsx`/`AnalyticsDashboard.tsx` (`aria-controls`
 * on the trigger) and the wrapper `AnalyticsDashboard.tsx` renders the actual
 * section into (its `id`). Only one group's wrapper ever holds real content at
 * a time; the id itself is always present so `aria-controls` never dangles.
 */
export const DUE_PANEL_ID = 'clm95-due-panel';
export const KPI_PANEL_ID = 'clm95-kpi-panel';
export const MIX_PANEL_ID = 'clm95-mix-panel';
